import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";

const MatchingCriteria = () => {
  const { user: authUser, fetchWithRetry, isLoading, logout } = useAuth();
  const [criteria, setCriteria] = useState({
    ageRange: { minAge: 18, maxAge: 100 },
    gender: "any",
    nationality: "any",
    occupation: "any",
    education: "any",
    religion: "any",
    maritalStatus: "any",
    hasChildren: "any",
    drinking: "any",
    smoking: "any",
    willingToRelocate: "any",
    zodiacSign: "any",
    lunarYear: "any",
    locationText: { country: "any", city: "any", district: "any", ward: "any" },
    currentLocation: { country: "any", city: "any", district: "any", ward: "any", coordinates: [0, 0] },
    appearance: {
      hairColor: "any",
      eyeColor: "any",
      height: { min: 0, max: 300 },
      weight: { min: 0, max: 300 },
      bodyType: "any",
      ethnicity: "any",
      bodyArt: "any",
      appearanceRating: "any",
    },
    lifestyle: {
      drinking: "any",
      smoking: "any",
      maritalStatus: "any",
      hasChildren: "any",
      numberOfChildren: "any",
      oldestChild: "any",
      youngestChild: "any",
      wantsChildren: "any",
      hasPets: "any",
      occupation: "any",
    },
    culture: {
      nationality: "any",
      education: "any",
      primaryLanguage: "any",
      englishAbility: "any",
      vietnameseAbility: "any",
      religion: "any",
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();

  // Hàm tải tiêu chí hiện tại
  const fetchCriteria = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithRetry({
        method: "get",
        url: "/api/users/profile",
      });
      const data = res.data || {};
      if (data && data._id) {
        const fetchedCriteria = data.matchingCriteria || {};
        setCriteria((prev) => ({
          ...prev,
          ...fetchedCriteria,
          ageRange: {
            minAge: fetchedCriteria.ageRange?.minAge || prev.ageRange.minAge,
            maxAge: fetchedCriteria.ageRange?.maxAge || prev.ageRange.maxAge,
          },
          locationText: {
            country: fetchedCriteria.locationText?.country || prev.locationText.country,
            city: fetchedCriteria.locationText?.city || prev.locationText.city,
            district: fetchedCriteria.locationText?.district || prev.locationText.district,
            ward: fetchedCriteria.locationText?.ward || prev.locationText.ward,
          },
          currentLocation: {
            country: fetchedCriteria.currentLocation?.country || prev.currentLocation.country,
            city: fetchedCriteria.currentLocation?.city || prev.currentLocation.city,
            district: fetchedCriteria.currentLocation?.district || prev.currentLocation.district,
            ward: fetchedCriteria.currentLocation?.ward || prev.currentLocation.ward,
            coordinates: fetchedCriteria.currentLocation?.coordinates || prev.currentLocation.coordinates,
          },
          appearance: {
            hairColor: fetchedCriteria.appearance?.hairColor || prev.appearance.hairColor,
            eyeColor: fetchedCriteria.appearance?.eyeColor || prev.appearance.eyeColor,
            height: {
              min: fetchedCriteria.appearance?.height?.min || prev.appearance.height.min,
              max: fetchedCriteria.appearance?.height?.max || prev.appearance.height.max,
            },
            weight: {
              min: fetchedCriteria.appearance?.weight?.min || prev.appearance.weight.min,
              max: fetchedCriteria.appearance?.weight?.max || prev.appearance.weight.max,
            },
            bodyType: fetchedCriteria.appearance?.bodyType || prev.appearance.bodyType,
            ethnicity: fetchedCriteria.appearance?.ethnicity || prev.appearance.ethnicity,
            bodyArt: fetchedCriteria.appearance?.bodyArt || prev.appearance.bodyArt,
            appearanceRating: fetchedCriteria.appearance?.appearanceRating || prev.appearance.appearanceRating,
          },
          lifestyle: {
            drinking: fetchedCriteria.lifestyle?.drinking || prev.lifestyle.drinking,
            smoking: fetchedCriteria.lifestyle?.smoking || prev.lifestyle.smoking,
            maritalStatus: fetchedCriteria.lifestyle?.maritalStatus || prev.lifestyle.maritalStatus,
            hasChildren: fetchedCriteria.lifestyle?.hasChildren || prev.lifestyle.hasChildren,
            numberOfChildren: fetchedCriteria.lifestyle?.numberOfChildren || prev.lifestyle.numberOfChildren,
            oldestChild: fetchedCriteria.lifestyle?.oldestChild || prev.lifestyle.oldestChild,
            youngestChild: fetchedCriteria.lifestyle?.youngestChild || prev.lifestyle.youngestChild,
            wantsChildren: fetchedCriteria.lifestyle?.wantsChildren || prev.lifestyle.wantsChildren,
            hasPets: fetchedCriteria.lifestyle?.hasPets || prev.lifestyle.hasPets,
            occupation: fetchedCriteria.lifestyle?.occupation || prev.lifestyle.occupation,
          },
          culture: {
            nationality: fetchedCriteria.culture?.nationality || prev.culture.nationality,
            education: fetchedCriteria.culture?.education || prev.culture.education,
            primaryLanguage: fetchedCriteria.culture?.primaryLanguage || prev.culture.primaryLanguage,
            englishAbility: fetchedCriteria.culture?.englishAbility || prev.culture.englishAbility,
            vietnameseAbility: fetchedCriteria.culture?.vietnameseAbility || prev.culture.vietnameseAbility,
            religion: fetchedCriteria.culture?.religion || prev.culture.religion,
          },
        }));
      } else {
        setError("Không thể tải tiêu chí từ server.");
      }
    } catch (error) {
      console.error("Lỗi khi tải tiêu chí:", error.response ? error.response.data : error.message);
      if (error.response?.status === 404) {
        setError("Người dùng không tồn tại trên server. Vui lòng kiểm tra lại tài khoản.");
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        setError("Phiên làm việc hết hạn hoặc token không hợp lệ. Vui lòng đăng nhập lại.");
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else {
        setError(error.response?.data?.message || "Không thể tải tiêu chí từ server.");
      }
    } finally {
      setLoading(false);
    }
  }, [fetchWithRetry, logout, navigate]);

  // Hàm tải thông báo từ API
  const fetchNotifications = useCallback(async () => {
    try {
      setNotifications(
        (await fetchWithRetry({
          method: "get",
          url: "/api/users/notifications",
        })).data
      );
    } catch (error) {
      console.error("Lỗi khi tải thông báo:", error);
    }
  }, [fetchWithRetry]);

  // Tải tiêu chí và thông báo khi người dùng đăng nhập
  useEffect(() => {
    if (isLoading) return;

    if (!authUser || !authUser._id) {
      navigate("/login");
      return;
    }

    fetchCriteria();
    fetchNotifications();
  }, [authUser, isLoading, navigate, fetchCriteria, fetchNotifications]);

  const unreadCount = 0;
  const unreadNotificationCount = notifications.filter((n) => !n.read).length;

  const handleToggleNotifications = (notificationId, action) => {
    if (action === "read") {
      setNotifications((prev) =>
        prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
      );
    } else if (action === "delete") {
      setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
    }
  };

  // Hàm xử lý thay đổi tiêu chí
  const handleCriteriaChange = (e) => {
    const { name, value } = e.target;
    if (name === "minAge" || name === "maxAge") {
      setCriteria((prev) => ({
        ...prev,
        ageRange: {
          ...prev.ageRange,
          [name]: parseInt(value) || 0,
        },
      }));
    } else if (name.startsWith("locationText.")) {
      const field = name.split(".")[1];
      setCriteria((prev) => ({
        ...prev,
        locationText: {
          ...prev.locationText,
          [field]: value,
        },
      }));
    } else if (name.startsWith("currentLocation.")) {
      const field = name.split(".")[1];
      setCriteria((prev) => ({
        ...prev,
        currentLocation: {
          ...prev.currentLocation,
          [field]: value,
        },
      }));
    } else if (name.startsWith("appearance.")) {
      const field = name.split(".")[1];
      if (field === "heightMin" || field === "heightMax") {
        setCriteria((prev) => ({
          ...prev,
          appearance: {
            ...prev.appearance,
            height: {
              ...prev.appearance.height,
              [field === "heightMin" ? "min" : "max"]: parseInt(value) || 0,
            },
          },
        }));
      } else if (field === "weightMin" || field === "weightMax") {
        setCriteria((prev) => ({
          ...prev,
          appearance: {
            ...prev.appearance,
            weight: {
              ...prev.appearance.weight,
              [field === "weightMin" ? "min" : "max"]: parseInt(value) || 0,
            },
          },
        }));
      } else {
        setCriteria((prev) => ({
          ...prev,
          appearance: {
            ...prev.appearance,
            [field]: value,
          },
        }));
      }
    } else if (name.startsWith("lifestyle.")) {
      const field = name.split(".")[1];
      setCriteria((prev) => ({
        ...prev,
        lifestyle: {
          ...prev.lifestyle,
          [field]: value,
        },
      }));
    } else if (name.startsWith("culture.")) {
      const field = name.split(".")[1];
      setCriteria((prev) => ({
        ...prev,
        culture: {
          ...prev.culture,
          [field]: value,
        },
      }));
    } else {
      setCriteria((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Hàm cập nhật tiêu chí phù hợp
  const handleUpdateCriteria = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await fetchWithRetry({
        method: "put",
        url: "/api/users/matching-criteria",
        data: criteria,
      });

      setSuccess("Cập nhật tiêu chí phù hợp thành công!");
      setLoading(false);
    } catch (err) {
      console.error("Lỗi khi cập nhật tiêu chí:", err.message);
      if (err.response?.status === 404) {
        setError("Người dùng không tồn tại trên server. Vui lòng đăng nhập lại.");
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        setError("Phiên làm việc hết hạn hoặc token không hợp lệ. Vui lòng đăng nhập lại.");
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else if (err.response?.status === 500) {
        setError(err.response?.data?.message || "Lỗi server khi cập nhật tiêu chí! Vui lòng thử lại sau.");
      } else {
        setError(err.message || "Lỗi không xác định khi cập nhật tiêu chí! Vui lòng thử lại.");
      }
      setLoading(false);
    }
  };

  if (isLoading || loading) {
    return <p className="text-center text-gray-600">Đang tải...</p>;
  }

  return (
    <div className="font-sans min-h-screen bg-gradient-to-b from-gray-100 to-gray-200">
      <Navbar
        unreadCount={unreadCount}
        unreadNotificationCount={unreadNotificationCount}
        notifications={notifications}
        onToggleNotifications={handleToggleNotifications}
      />

      <div className="pt-20 max-w-5xl mx-auto p-6">
        <h2 className="text-4xl font-bold text-center text-gray-800 mb-8">Cập Nhật Tiêu Chí Phù Hợp</h2>

        {error && <p className="text-red-500 text-center mb-4">{error}</p>}
        {success && <p className="text-green-500 text-center mb-4">{success}</p>}

        <div className="bg-white p-8 rounded-lg shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Độ tuổi */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">Độ tuổi tối thiểu:</label>
              <input
                type="number"
                name="minAge"
                value={criteria.ageRange?.minAge || 18}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
                min="18"
                max="100"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Độ tuổi tối đa:</label>
              <input
                type="number"
                name="maxAge"
                value={criteria.ageRange?.maxAge || 100}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
                min="18"
                max="100"
              />
            </div>

            {/* Giới tính */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">Giới tính:</label>
              <select
                name="gender"
                value={criteria.gender || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="male">Nam</option>
                <option value="female">Nữ</option>
                <option value="other">Khác</option>
              </select>
            </div>

            {/* Vị trí */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">Quốc gia:</label>
              <select
                name="locationText.country"
                value={criteria.locationText?.country || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Việt Nam">Việt Nam</option>
                <option value="Afghanistan">Afghanistan</option>
                <option value="Ai Cập">Ai Cập</option>
                <option value="Albania">Albania</option>
                <option value="Algeria">Algeria</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Thành phố:</label>
              <select
                name="locationText.city"
                value={criteria.locationText?.city || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Hồ Chí Minh">Hồ Chí Minh</option>
                <option value="Hà Nội">Hà Nội</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Quận/Huyện:</label>
              <select
                name="locationText.district"
                value={criteria.locationText?.district || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Quận 1">Quận 1</option>
                <option value="Quận 3">Quận 3</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Xã/Phường:</label>
              <input
                type="text"
                name="locationText.ward"
                value={criteria.locationText?.ward || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
                placeholder="Nhập xã/phường hoặc để trống"
              />
            </div>

            {/* Diện mạo */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">Chiều cao tối thiểu (cm):</label>
              <input
                type="number"
                name="appearance.heightMin"
                value={criteria.appearance?.height?.min || 0}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
                min="0"
                max="300"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Chiều cao tối đa (cm):</label>
              <input
                type="number"
                name="appearance.heightMax"
                value={criteria.appearance?.height?.max || 300}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
                min="0"
                max="300"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Cân nặng tối thiểu (kg):</label>
              <input
                type="number"
                name="appearance.weightMin"
                value={criteria.appearance?.weight?.min || 0}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
                min="0"
                max="300"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Cân nặng tối đa (kg):</label>
              <input
                type="number"
                name="appearance.weightMax"
                value={criteria.appearance?.weight?.max || 300}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
                min="0"
                max="300"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Màu tóc:</label>
              <select
                name="appearance.hairColor"
                value={criteria.appearance?.hairColor || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Hói / Cạo">Hói / Cạo</option>
                <option value="Nâu">Nâu</option>
                <option value="Nâu sáng">Nâu sáng</option>
                <option value="Vàng">Vàng</option>
                <option value="Xám / Trắng">Xám / Trắng</option>
                <option value="Đen">Đen</option>
                <option value="Đỏ">Đỏ</option>
                <option value="Thường xuyên thay đổi">Thường xuyên thay đổi</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Màu mắt:</label>
              <select
                name="appearance.eyeColor"
                value={criteria.appearance?.eyeColor || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Nâu">Nâu</option>
                <option value="Nâu lục nhạt">Nâu lục nhạt</option>
                <option value="Xanh lam">Xanh lam</option>
                <option value="Xanh lục">Xanh lục</option>
                <option value="Xám">Xám</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Thân hình:</label>
              <select
                name="appearance.bodyType"
                value={criteria.appearance?.bodyType || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="slim">Gầy</option>
                <option value="athletic">Lực lưỡng</option>
                <option value="average">Trung bình</option>
                <option value="heavy">Nặng ký</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Sắc tộc:</label>
              <select
                name="appearance.ethnicity"
                value={criteria.appearance?.ethnicity || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Châu Á">Châu Á</option>
                <option value="Da trắng">Da trắng</option>
                <option value="La tinh">La tinh</option>
                <option value="Lai">Lai</option>
                <option value="Phi">Phi</option>
                <option value="Thái Bình Dương">Thái Bình Dương</option>
                <option value="Ả rập (Trung Đông)">Ả rập (Trung Đông)</option>
                <option value="Ấn Độ">Ấn Độ</option>
                <option value="Khác">Khác</option>
                <option value="Không thích nói">Không thích nói</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Hình xăm/Khuyên:</label>
              <select
                name="appearance.bodyArt"
                value={criteria.appearance?.bodyArt || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Hình xăm">Hình xăm</option>
                <option value="Khuyên">Khuyên</option>
                <option value="Khuyên tai">Khuyên tai</option>
                <option value="Đóng dấu">Đóng dấu</option>
                <option value="Khác">Khác</option>
                <option value="Không">Không</option>
                <option value="Không thích nói">Không thích nói</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Đánh giá diện mạo:</label>
              <select
                name="appearance.appearanceRating"
                value={criteria.appearance?.appearanceRating || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Dưới mức trung bình">Dưới mức trung bình</option>
                <option value="Trung bình">Trung bình</option>
                <option value="Thu hút">Thu hút</option>
                <option value="Rất thu hút">Rất thu hút</option>
              </select>
            </div>

            {/* Phong cách sống */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">Uống rượu:</label>
              <select
                name="lifestyle.drinking"
                value={criteria.lifestyle?.drinking || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="yes">Có</option>
                <option value="no">Không</option>
                <option value="occasionally">Thỉnh thoảng</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Hút thuốc:</label>
              <select
                name="lifestyle.smoking"
                value={criteria.lifestyle?.smoking || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="yes">Có</option>
                <option value="no">Không</option>
                <option value="occasionally">Thỉnh thoảng</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Tình trạng hôn nhân:</label>
              <select
                name="lifestyle.maritalStatus"
                value={criteria.lifestyle?.maritalStatus || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="single">Độc thân</option>
                <option value="married">Đã kết hôn</option>
                <option value="divorced">Ly hôn</option>
                <option value="widowed">Góa</option>
                <option value="other">Khác</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Có con:</label>
              <select
                name="lifestyle.hasChildren"
                value={criteria.lifestyle?.hasChildren || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="yes">Có</option>
                <option value="no">Không</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Số lượng con:</label>
              <select
                name="lifestyle.numberOfChildren"
                value={criteria.lifestyle?.numberOfChildren || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5+">5 trở lên</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Con lớn nhất:</label>
              <select
                name="lifestyle.oldestChild"
                value={criteria.lifestyle?.oldestChild || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="0-5">0-5 tuổi</option>
                <option value="6-10">6-10 tuổi</option>
                <option value="11-15">11-15 tuổi</option>
                <option value="16-20">16-20 tuổi</option>
                <option value="21+">21 tuổi trở lên</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Con nhỏ nhất:</label>
              <select
                name="lifestyle.youngestChild"
                value={criteria.lifestyle?.youngestChild || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="0-5">0-5 tuổi</option>
                <option value="6-10">6-10 tuổi</option>
                <option value="11-15">11-15 tuổi</option>
                <option value="16-20">16-20 tuổi</option>
                <option value="21+">21 tuổi trở lên</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Muốn có (thêm) con:</label>
              <select
                name="lifestyle.wantsChildren"
                value={criteria.lifestyle?.wantsChildren || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="yes">Có</option>
                <option value="no">Không</option>
                <option value="not_sure">Không chắc</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Có vật nuôi:</label>
              <select
                name="lifestyle.hasPets"
                value={criteria.lifestyle?.hasPets || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Bò sát">Bò sát</option>
                <option value="Chim chóc">Chim chóc</option>
                <option value="Chó">Chó</option>
                <option value="Cá">Cá</option>
                <option value="Hamster">Hamster</option>
                <option value="Mèo">Mèo</option>
                <option value="Ngựa">Ngựa</option>
                <option value="Thú quý hiếm">Thú quý hiếm</option>
                <option value="Thỏ">Thỏ</option>
                <option value="Khác">Khác</option>
                <option value="Không có vật nuôi">Không có vật nuôi</option>
                <option value="Không thích nói">Không thích nói</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Nghề nghiệp:</label>
              <select
                name="lifestyle.occupation"
                value={criteria.lifestyle?.occupation || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Bán hàng / Tiếp thị">Bán hàng / Tiếp thị</option>
                <option value="Chữa cháy / Hành pháp / Bảo vệ">Chữa cháy / Hành pháp / Bảo vệ</option>
                <option value="Giao thông vận tải">Giao thông vận tải</option>
                <option value="Hành chính / Thư ký">Hành chính / Thư ký</option>
                <option value="Làm về pháp luật">Làm về pháp luật</option>
                <option value="Người giúp việc">Người giúp việc</option>
                <option value="Phi lợi nhuận / Giáo sĩ / Công tác xã hội">Phi lợi nhuận / Giáo sĩ / Công tác xã hội</option>
                <option value="Sinh viên">Sinh viên</option>
                <option value="Bán lẻ / Dịch vụ thực phẩm">Bán lẻ / Dịch vụ thực phẩm</option>
                <option value="Công nghệ thông tin / Truyền thông">Công nghệ thông tin / Truyền thông</option>
                <option value="Giáo dục">Giáo dục</option>
                <option value="Không có việc làm / Ở nhà">Không có việc làm / Ở nhà</option>
                <option value="Nghệ thuật / Sáng tạo / Biểu diễn">Nghệ thuật / Sáng tạo / Biểu diễn</option>
                <option value="Người lao động / Công nhân nhà máy">Người lao động / Công nhân nhà máy</option>
                <option value="Quân đội">Quân đội</option>
                <option value="Thể thao / Giải trí">Thể thao / Giải trí</option>
                <option value="Chính trị / Nhà nước / Dịch vụ dân sự">Chính trị / Nhà nước / Dịch vụ dân sự</option>
                <option value="Du lịch / Khách sạn">Du lịch / Khách sạn</option>
                <option value="Giải trí / Truyền thông">Giải trí / Truyền thông</option>
                <option value="Kỹ thuật / Khoa học / Kỹ sư">Kỹ thuật / Khoa học / Kỹ sư</option>
                <option value="Nghỉ hưu">Nghỉ hưu</option>
                <option value="Nông nghiệp">Nông nghiệp</option>
                <option value="Quảng cáo / Truyền thông">Quảng cáo / Truyền thông</option>
                <option value="Thợ cắt tóc">Thợ cắt tóc</option>
                <option value="Trông trẻ">Trông trẻ</option>
                <option value="Tài chính / Ngân hàng / Tự kinh doanh">Tài chính / Ngân hàng / Tự kinh doanh</option>
                <option value="Xây dựng / Giao dịch">Xây dựng / Giao dịch</option>
                <option value="Y khoa / Nha khoa / Thú y">Y khoa / Nha khoa / Thú y</option>
              </select>
            </div>

            {/* Văn hóa */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">Quốc tịch:</label>
              <select
                name="culture.nationality"
                value={criteria.culture?.nationality || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Việt Nam">Việt Nam</option>
                <option value="Afghanistan">Afghanistan</option>
                <option value="Ai Cập">Ai Cập</option>
                <option value="Albania">Albania</option>
                <option value="Algeria">Algeria</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Trình độ văn hóa:</label>
              <select
                name="culture.education"
                value={criteria.culture?.education || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Cấp 1">Cấp 1</option>
                <option value="Bằng cấp 3 / Cấp 2">Bằng cấp 3 / Cấp 2</option>
                <option value="Bằng trung học phổ thông">Bằng trung học phổ thông</option>
                <option value="Bằng trung cấp nghề">Bằng trung cấp nghề</option>
                <option value="Bằng cử nhân">Bằng cử nhân</option>
                <option value="Bằng thạc sỹ">Bằng thạc sỹ</option>
                <option value="Tiến sĩ">Tiến sĩ</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Ngôn ngữ chính:</label>
              <select
                name="culture.primaryLanguage"
                value={criteria.culture?.primaryLanguage || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Tiếng Việt">Tiếng Việt</option>
                <option value="Tiếng Anh">Tiếng Anh</option>
                <option value="Chinese (Mandarin)">Chinese (Mandarin)</option>
                <option value="Tiếng Pháp">Tiếng Pháp</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Khả năng tiếng Anh:</label>
              <select
                name="culture.englishAbility"
                value={criteria.culture?.englishAbility || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Không">Không</option>
                <option value="Thỉnh thoảng">Thỉnh thoảng</option>
                <option value="Tốt">Tốt</option>
                <option value="Rất tốt">Rất tốt</option>
                <option value="Lưu loát">Lưu loát</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Khả năng tiếng Việt:</label>
              <select
                name="culture.vietnameseAbility"
                value={criteria.culture?.vietnameseAbility || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Không">Không</option>
                <option value="Thỉnh thoảng">Thỉnh thoảng</option>
                <option value="Tốt">Tốt</option>
                <option value="Rất tốt">Rất tốt</option>
                <option value="Lưu loát">Lưu loát</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Tôn giáo:</label>
              <select
                name="culture.religion"
                value={criteria.culture?.religion || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Phật giáo">Phật giáo</option>
                <option value="Thiên Chúa giáo">Thiên Chúa giáo</option>
                <option value="Hồi giáo">Hồi giáo</option>
                <option value="Đạo giáo">Đạo giáo</option>
                <option value="Tích-khắc giáo">Tích-khắc giáo</option>
                <option value="Đạo Shintō">Đạo Shintō</option>
                <option value="Không có đạo">Không có đạo</option>
                <option value="Không thích nói">Không thích nói</option>
              </select>
            </div>

            {/* Chòm sao và năm sinh âm lịch */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">Chòm sao:</label>
              <select
                name="zodiacSign"
                value={criteria.zodiacSign || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Bò Cạp">Bò Cạp</option>
                <option value="Bảo Bình">Bảo Bình</option>
                <option value="Cự Giải">Cự Giải</option>
                <option value="Dương Cưu">Dương Cưu</option>
                <option value="Kim Ngưu">Kim Ngưu</option>
                <option value="Ma Kết">Ma Kết</option>
                <option value="Nhân Mã">Nhân Mã</option>
                <option value="Song Ngư">Song Ngư</option>
                <option value="Song Sinh">Song Sinh</option>
                <option value="Sư Tử">Sư Tử</option>
                <option value="Thiên Bình">Thiên Bình</option>
                <option value="Xử Nữ">Xử Nữ</option>
                <option value="Không biết">Không biết</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Năm sinh âm lịch:</label>
              <select
                name="lunarYear"
                value={criteria.lunarYear || "any"}
                onChange={handleCriteriaChange}
                className="w-full border rounded-lg p-2"
              >
                <option value="any">Bất kỳ</option>
                <option value="Dần">Dần</option>
                <option value="Dậu">Dậu</option>
                <option value="Hợi">Hợi</option>
                <option value="Mão">Mão</option>
                <option value="Mùi">Mùi</option>
                <option value="Ngọ">Ngọ</option>
                <option value="Sửu">Sửu</option>
                <option value="Thân">Thân</option>
                <option value="Thìn">Thìn</option>
                <option value="Tuất">Tuất</option>
                <option value="Tý">Tý</option>
                <option value="Tỵ">Tỵ</option>
                <option value="Không biết">Không biết</option>
              </select>
            </div>
          </div>

          {/* Nút lưu và hủy */}
          <div className="flex justify-end gap-4 mt-6">
            <Link
              to="/profile"
              className="px-4 py-2 bg-gray-500 text-white rounded-lg shadow hover:bg-gray-600"
            >
              Hủy
            </Link>
            <button
              onClick={handleUpdateCriteria}
              className="px-4 py-2 bg-green-500 text-white rounded-lg shadow hover:bg-green-600"
              disabled={loading}
            >
              {loading ? "Đang lưu..." : "Lưu"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchingCriteria;